/**
 * Axios API Configuration
 * 
 * Centralized HTTP client with interceptors for authentication,
 * error handling, and request/response transformations
 */

import axios, { AxiosError, AxiosResponse } from 'axios';

// Extend the Axios config type to include retry control
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean; // Prevents infinite retry loops
    skipTokenRefresh?: boolean; // Skip automatic token refresh for this request
  }
  
  interface AxiosRequestConfig {
    skipTokenRefresh?: boolean; // Skip automatic token refresh for this request
  }
}

// Create axios instance with default configuration
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
  timeout: 10000,
  withCredentials: true, // ✅ Enable cookies for authentication
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * Logs requests in development mode
 * Note: Authentication is now handled via httpOnly cookies automatically
 */
api.interceptors.request.use(
  (config) => {
    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles automatic token refresh and error responses using cookies
 * 
 * Token refresh can be controlled per request using the `skipTokenRefresh` config option:
 * - Set to `true` for auth endpoints to prevent infinite loops
 * - Set to `true` for requests that shouldn't trigger automatic retry
 * - Omit or set to `false` for normal protected endpoints
 */
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    // Log error details for debugging
    console.error('❌ API Error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      response: error.response?.data,
    });
    
    // Handle 401 Unauthorized - Token expired (but only if refresh is allowed)
    if (error.response?.status === 401 && 
        originalRequest && 
        !originalRequest._retry && 
        !originalRequest.skipTokenRefresh) {
      
      originalRequest._retry = true;
      
      try {
        console.log('🔄 Attempting token refresh...');
        
        // Try to refresh token using cookies
        // The refresh token cookie will be sent automatically
        await api.post('/auth/refresh', {}, {
          skipTokenRefresh: true, // Prevent infinite loops
        });
        
        console.log('✅ Token refresh successful, retrying original request...');
        
        // If refresh successful, retry original request
        // New access token cookie will be set automatically
        return api(originalRequest);
        
      } catch (refreshError) {
        // Refresh failed - dispatch event for auth store to handle logout
        console.error('❌ Token refresh failed:', refreshError);
        
        // Dispatch custom event for auth store to handle
        window.dispatchEvent(new CustomEvent('auth:token-expired', {
          detail: { 
            originalError: error,
            refreshError: refreshError 
          }
        }));
        
        return Promise.reject(refreshError);
      }
    }
    
    // Handle 500 errors on refresh endpoint specifically
    if (error.response?.status === 500 && 
        originalRequest?.url?.includes('/auth/refresh')) {
      console.error('❌ Refresh endpoint returned 500 - server error or invalid refresh token');
      
      // Dispatch event to force logout on server errors during refresh
      window.dispatchEvent(new CustomEvent('auth:refresh-server-error', {
        detail: { error: error }
      }));
    }
    
    return Promise.reject(error);
  }
);

/**
 * API Error Types
 */
export interface ApiError {
  message: string;
  code?: string;
  statusCode?: number;
  details?: Record<string, any>;
}

/**
 * Generic API Response Type
 */
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
  timestamp: string;
}

/**
 * Utility function to handle API errors consistently
 */
export const handleApiError = (error: AxiosError): ApiError => {
  if (error.response) {
    // Server responded with error status
    const responseData = error.response.data as any;
    return {
      message: responseData?.message || 'Server Error',
      code: responseData?.code,
      statusCode: error.response.status,
      details: responseData?.details,
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      message: 'Network Error - No response from server',
      statusCode: 0,
    };
  } else {
    // Something else happened
    return {
      message: error.message || 'Unknown Error',
    };
  }
};

export default api;